import { useMount, useThrottleFn } from 'ahooks';
import { message } from 'antd';
import { useEffect, useRef, useState } from 'react';

import { useUserStore } from '@/store/useUser';

import { handleParams, handleResultData } from '.';
import { IPage, IPageAction, MockData } from './commonInterface';
function awaitWrap<T, U = any>(
  promise: Promise<T>,
): Promise<[U | null, T | null]> {
  return promise
    .then<[null, T]>((data: T) => [null, data])
    .catch<[U, null]>((err) => [err, null]);
}
export function useSearchList<
  RecordType,
  FormValues extends object = any,
  SearchValues = FormValues,
>({
  otherSearchValues = {},
  initialValues = {},
  mockData = { data: [] },
  dataSourceKey = 'data',
  isSendRequest = true,
  hideMock = true,
  defaultPageSize = 25,
  onSuccess,
  onError,
  getPageNoFn,
  getApi,
}: {
  otherSearchValues?: any;
  initialValues?: Partial<FormValues>;
  mockData?: MockData<RecordType>;
  dataSourceKey: string;
  isSendRequest?: boolean;
  hideMock?: boolean;
  defaultPageSize?: number;
  getPageNoFn?: () => any;
  onSuccess?: (...params: any[]) => void;
  onError?: (...params: any[]) => void;
  getApi: (params?: SearchValues) => any;
}) {
  const [dataList, setDataList] = useState<RecordType[]>([]);
  const [params, setParams] = useState<SearchValues>({
    limit: defaultPageSize,
    page: 1,
    ...initialValues,
    ...otherSearchValues,
  });

  const [pageParams, setPageParams] = useState<IPage>({});

  const [isLoading, setLoading] = useState<boolean>(false);
  const paramsState = useRef(params);
  const mockDataState = useRef(mockData);

  const { msgError } = useUserStore();

  useMount(() => {
    if (isSendRequest) {
      ajax();
    }
  });

  useEffect(() => {
    if (hideMock) {
      mockDataState.current = { data: [] };
    }
  }, [hideMock]);

  const ajaxFn = async (
    obj?: Partial<SearchValues>,
    paramsFullCover?: boolean,
    afterHandle?: () => void,
  ) => {
    const currentParams = {
      ...params,
    };

    paramsState.current = handleParams<SearchValues, boolean>(
      obj,
      currentParams,
      paramsFullCover,
    );
    setLoading(true);
    const [err, rs] = await awaitWrap<any>(getApi(paramsState.current));
    setLoading(false);
    if (err) {
      // message.error(err?.ret_msg);
      msgError(err);
      return;
    }
    setParams(paramsState.current);

    handleResultData<RecordType>(rs, dataSourceKey, mockDataState.current, {
      message,
      setDataList,
      setPageParams,
      onSuccess,
      onError,
      afterHandle,
    });
  };

  const { run: ajax } = useThrottleFn(ajaxFn);

  const setPageNo = (evt: number | IPageAction) => {
    const pageInfo = getPageNoFn && getPageNoFn();
    if (typeof evt === 'number') {
      return;
    } else if (evt.action === 'prePage') {
      if (pageInfo && pageInfo.resetPageNo) {
        pageInfo.minusPageNo();
      }
    } else if (evt.action === 'nextPage') {
      if (pageInfo && pageInfo.resetPageNo) {
        pageInfo.plusPageNo();
      }
    }
  };

  function turnPages(evt: number | IPageAction) {
    const currentParams: any = {
      ...paramsState.current,
    };
    // TODO: error情况同步问题
    if (typeof evt === 'number') {
      currentParams.limit = evt;
      currentParams.page = 1;
    } else if (evt.action === 'prePage') {
      currentParams.page -= 1;
    } else if (evt.action === 'nextPage') {
      currentParams.page += 1;
    }
    const afterHandle = () => setPageNo(evt);

    ajax(currentParams, true, afterHandle);
  }

  return {
    dataSource: dataList,
    searchParams: params,
    pageParams,
    isLoading,
    ajax,
    turnPages,
    setDataList,
  };
}
